import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi, supportApi } from '../services/api';
import { CLIENT_KEY_STORAGE, SCREENS } from '../lib/screenRegistry';
import {
  GENDER_OPTIONS,
  LANGUAGE_OPTIONS,
  formatVoiceProfileLabel,
  resolveVoiceFromMatrix,
  previewVoiceSample as playVoicePreviewSample,
} from '../lib/voiceProfile';
import './OrgAdmin.css';

const SIDEBAR_SECTIONS = ['Overview', 'Subscription', 'Enrollment', 'Policy Vault', 'Guardian Feed', 'Support'];
const ENROLLMENT_SUB_NAV = ['Mentor List', 'Archives'] as const;
type EnrollmentSubview = 'default' | 'mentor-list' | 'archives';

interface OrgMentor {
  id: number;
  name: string;
  phone: string | null;
  email: string | null;
  background_brief: string | null;
  activation_key: string;
  status: string;
  archived: number;
  created_at: string;
  menteeCount?: number;
}

interface OrgMenteeKey {
  mentee_token: string;
  status: string;
  account_state: string;
  last_active: string;
  mentee_name?: string;
  mentee_email?: string;
  mentor_name?: string;
  mentor_phone?: string;
  mentor_email?: string;
  mentor_id?: number | null;
}

interface GuardianAlert {
  id: number;
  mentee_token: string;
  crisis_type: string;
  mentor_email: string | null;
  mentor_notified: number;
  created_at: string;
  mentee_name: string | null;
  mentor_name: string | null;
}

type VaultFileCategory = 'General' | 'Emergency Protocol';

interface VaultFileEntry {
  id: string;
  filename: string;
  uploadedAt: string;
  category: VaultFileCategory;
  vaultPath: string;
}

const formatOrgVaultFolder = (orgToken?: string) => {
  if (!orgToken) return '[ELT_ORG_0000]';
  return `[${orgToken.replace(/-/g, '_')}]`;
};

const buildVaultPath = (
  orgToken: string | undefined,
  category: VaultFileCategory,
  filename: string,
) => {
  const folder = formatOrgVaultFolder(orgToken);
  const segment = category === 'Emergency Protocol' ? 'red-phone-protocols' : 'general-documents';
  return `Mentor Project Vault / Mentoring Org Folder / ${folder} / ${segment} / ${filename}`;
};

export default function OrgAdmin() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('Overview');
  const [enrollmentSubview, setEnrollmentSubview] = useState<EnrollmentSubview>('default');
  const [dashboard, setDashboard] = useState<{
    org?: {
      id?: number;
      company_name: string;
      seat_count: number;
      admin_name: string;
      admin_email?: string;
      address?: string;
      phone?: string;
      org_token?: string;
      product_code?: string;
    };
    keys?: OrgMenteeKey[];
    mentors?: OrgMentor[];
    unassignedMentees?: OrgMenteeKey[];
    archivedMentors?: OrgMentor[];
    archivedMentees?: OrgMenteeKey[];
    guardianFeed?: GuardianAlert[];
    activeSeats?: number;
  } | null>(null);
  const [guardianFeed, setGuardianFeed] = useState<GuardianAlert[]>([]);
  const [newKey, setNewKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [enrollError, setEnrollError] = useState('');
  const [enrollForm, setEnrollForm] = useState({
    menteeName: '',
    businessName: '',
    address: '',
    contactPhone: '',
    menteeEmail: '',
    businessDescription: '',
    dateEnrolled: '',
    mentorName: '',
    mentorPhone: '',
    mentorEmail: '',
    languageTarget: '',
    aiMentorGender: '',
  });
  const [supportForm, setSupportForm] = useState({
    name: '',
    email: '',
    phone: '',
    description: '',
  });
  const [supportFiles, setSupportFiles] = useState<File[]>([]);
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportError, setSupportError] = useState('');
  const [supportSuccess, setSupportSuccess] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [vaultFiles, setVaultFiles] = useState<VaultFileEntry[]>([]);
  const [vaultDragTarget, setVaultDragTarget] = useState<'general' | 'emergency' | null>(null);
  const [redPhoneContacts, setRedPhoneContacts] = useState({
    emergencyContactName: '',
    emergencyLeadEmail: '',
    escalationPhone: '',
  });
  const [profileForm, setProfileForm] = useState({
    contactName: '',
    contactTitle: '',
    street: '',
    state: '',
    country: '',
    postcode: '',
    contactPhone: '',
    contactEmail: '',
    website: '',
  });
  const [clientKey, setClientKey] = useState('');
  const [mentorForm, setMentorForm] = useState({
    name: '',
    phone: '',
    email: '',
    backgroundBrief: '',
  });
  const [mentorLoading, setMentorLoading] = useState(false);
  const [mentorError, setMentorError] = useState('');
  const [copiedKey, setCopiedKey] = useState('');
  const [archiveActionLoading, setArchiveActionLoading] = useState<string | null>(null);

  const loadDashboard = useCallback(() => {
    return authApi.orgDashboard().then((data) => {
      setDashboard(data);
      if (data.guardianFeed) setGuardianFeed(data.guardianFeed);
    });
  }, []);

  useEffect(() => {
    const storedKey = sessionStorage.getItem(CLIENT_KEY_STORAGE);
    if (storedKey) {
      setClientKey(storedKey);
    }
  }, []);

  useEffect(() => {
    loadDashboard().catch(() => {
      if (!import.meta.env.DEV) navigate('/portal-login');
    });
  }, [loadDashboard, navigate]);

  useEffect(() => {
    if (!dashboard?.org) return;
    const org = dashboard.org;
    setProfileForm({
      contactName: org.admin_name || '',
      contactTitle: '',
      street: org.address || '',
      state: '',
      country: '',
      postcode: '',
      contactPhone: org.phone || '',
      contactEmail: org.admin_email || '',
      website: '',
    });
  }, [dashboard]);

  useEffect(() => {
    if (activeSection !== 'Guardian Feed') return;
    const poll = () => {
      authApi.guardianFeed().then((data) => setGuardianFeed(data.guardianFeed || [])).catch(() => {});
    };
    poll();
    const interval = setInterval(poll, 5000);
    return () => clearInterval(interval);
  }, [activeSection]);

  const updateProfile = (field: string, value: string) => {
    setProfileForm((f) => ({ ...f, [field]: value }));
  };

  const formatOrgId = (id?: number) =>
    id ? `ELT-N00-${String(id).padStart(4, '0')}` : 'ELT-N00-0001';

  const isEmergencyProtocolUploaded = vaultFiles.some(
    (file) => file.category === 'Emergency Protocol',
  );

  const addVaultFiles = (files: FileList | null, category: VaultFileCategory) => {
    if (!files?.length) return;
    const orgToken = dashboard?.org?.org_token;
    const uploadedAt = new Date().toLocaleString();
    const newEntries: VaultFileEntry[] = Array.from(files).map((file) => ({
      id: `${category}-${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      filename: file.name,
      uploadedAt,
      category,
      vaultPath: buildVaultPath(orgToken, category, file.name),
    }));

    setVaultFiles((prev) => {
      if (category === 'Emergency Protocol') {
        return [...prev.filter((entry) => entry.category !== 'Emergency Protocol'), ...newEntries];
      }
      return [...prev, ...newEntries];
    });
  };

  const updateRedPhoneContact = (field: string, value: string) => {
    setRedPhoneContacts((f) => ({ ...f, [field]: value }));
  };

  const resetRedPhoneContacts = () => {
    setRedPhoneContacts({
      emergencyContactName: '',
      emergencyLeadEmail: '',
      escalationPhone: '',
    });
  };

  const updateRedPhoneContacts = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const updateEnroll = (field: string, value: string) => {
    setEnrollForm((f) => ({ ...f, [field]: value }));
  };

  const resetEnrollForm = () => {
    setEnrollForm({
      menteeName: '',
      businessName: '',
      address: '',
      contactPhone: '',
      menteeEmail: '',
      businessDescription: '',
      dateEnrolled: '',
      mentorName: '',
      mentorPhone: '',
      mentorEmail: '',
      languageTarget: '',
      aiMentorGender: '',
    });
    setNewKey('');
    setEnrollError('');
  };

  const simulateCrmDataPush = () => {
    const today = new Date().toISOString().slice(0, 10);
    setEnrollForm((f) => ({
      ...f,
      menteeName: 'John Citizen',
      businessName: 'Citizen Consulting Pty Ltd',
      address: '123 Ocean Drive, Noosa Heads QLD 4567',
      contactPhone: '+61 412 345 678',
      menteeEmail: 'john@citizenconsulting.com.au',
      businessDescription: 'B2B Professional Services and Scale-up Advisory Operations',
      dateEnrolled: today,
      languageTarget: '',
      aiMentorGender: '',
      mentorName: '',
      mentorPhone: '',
      mentorEmail: '',
    }));
    setEnrollError('');
  };

  const handleEnrollmentVoicePreview = async () => {
    const entry = resolveVoiceFromMatrix(enrollForm.languageTarget, enrollForm.aiMentorGender);
    if (!entry) return;

    try {
      await playVoicePreviewSample(entry.voiceId, entry.name, {
        language: entry.language,
        gender: entry.gender,
        modelId: entry.modelId,
      });
    } catch (error) {
      console.error('[Voice preview]', error);
    }
  };

  const updateSupport = (field: string, value: string) => {
    setSupportForm((f) => ({ ...f, [field]: value }));
    setSupportSuccess('');
  };

  const addSupportFiles = (files: FileList | null) => {
    if (!files) return;
    setSupportFiles((prev) => [...prev, ...Array.from(files)]);
    setSupportSuccess('');
  };

  const readFileAsBase64 = (file: File) =>
    new Promise<{ filename: string; mimeType: string; size: number; data: string }>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve({
          filename: file.name,
          mimeType: file.type || 'application/octet-stream',
          size: file.size,
          data: result.split(',')[1] || '',
        });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const submitSupportTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    setSupportLoading(true);
    setSupportError('');
    setSupportSuccess('');
    try {
      const attachments = await Promise.all(supportFiles.map(readFileAsBase64));
      const result = await supportApi.submitTicket({
        ...supportForm,
        attachments,
      });
      setSupportSuccess(result.message || 'Support ticket successfully transmitted to Mentology Operations.');
      setSupportForm({ name: '', email: '', phone: '', description: '' });
      setSupportFiles([]);
    } catch (err: unknown) {
      setSupportError((err as { error?: string }).error || 'Support ticket submission failed.');
    } finally {
      setSupportLoading(false);
    }
  };

  const updateMentorForm = (field: string, value: string) => {
    setMentorForm((f) => ({ ...f, [field]: value }));
    setMentorError('');
  };

  const resetMentorForm = () => {
    setMentorForm({ name: '', phone: '', email: '', backgroundBrief: '' });
    setMentorError('');
  };

  const createMentor = async (e: React.FormEvent) => {
    e.preventDefault();
    setMentorLoading(true);
    setMentorError('');
    try {
      await authApi.createMentor(mentorForm);
      resetMentorForm();
      await loadDashboard();
    } catch (err: unknown) {
      setMentorError((err as { error?: string }).error || 'Mentor creation failed.');
    } finally {
      setMentorLoading(false);
    }
  };

  const copyMentorKey = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(''), 2000);
    } catch {
      setCopiedKey('');
    }
  };

  const handleAssignMentee = async (mentorId: number, menteeToken: string) => {
    if (!menteeToken) return;
    try {
      await authApi.assignMenteeToMentor(mentorId, menteeToken);
      await loadDashboard();
    } catch (err: unknown) {
      setMentorError((err as { error?: string }).error || 'Mentee assignment failed.');
    }
  };

  const handleArchiveMentor = async (mentorId: number) => {
    setArchiveActionLoading(`mentor-${mentorId}`);
    try {
      await authApi.archiveMentor(mentorId);
      await loadDashboard();
    } catch (err: unknown) {
      setMentorError((err as { error?: string }).error || 'Mentor archive failed.');
    } finally {
      setArchiveActionLoading(null);
    }
  };

  const handleRestoreMentor = async (mentorId: number) => {
    setArchiveActionLoading(`restore-mentor-${mentorId}`);
    try {
      await authApi.restoreMentor(mentorId);
      await loadDashboard();
    } catch (err: unknown) {
      setMentorError((err as { error?: string }).error || 'Mentor restore failed.');
    } finally {
      setArchiveActionLoading(null);
    }
  };

  const handleRestoreMentee = async (menteeToken: string) => {
    setArchiveActionLoading(`restore-mentee-${menteeToken}`);
    try {
      await authApi.restoreMentee(menteeToken);
      await loadDashboard();
    } catch (err: unknown) {
      setMentorError((err as { error?: string }).error || 'Mentee restore failed.');
    } finally {
      setArchiveActionLoading(null);
    }
  };

  const handleArchiveMentee = async (menteeToken: string) => {
    setArchiveActionLoading(`archive-mentee-${menteeToken}`);
    try {
      await authApi.archiveMentee(menteeToken);
      await loadDashboard();
    } catch (err: unknown) {
      setEnrollError((err as { error?: string }).error || 'Mentee archive failed.');
    } finally {
      setArchiveActionLoading(null);
    }
  };

  const enrollMentee = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setEnrollError('');
    setNewKey('');
    try {
      const result = await authApi.enrollMentee({
        menteeName: enrollForm.menteeName,
        menteeEmail: enrollForm.menteeEmail,
        mentorName: enrollForm.mentorName,
        mentorPhone: enrollForm.mentorPhone,
        mentorEmail: enrollForm.mentorEmail,
      });
      setNewKey(result.menteeToken);
      await loadDashboard();
    } catch (err: unknown) {
      setEnrollError((err as { error?: string }).error || 'Mentee enrollment failed.');
    } finally {
      setLoading(false);
    }
  };

  const renderPanel = () => {
    switch (activeSection) {
      case 'Overview': {
        const seatLimit = dashboard?.org?.seat_count ?? 10;
        const activeSeats = dashboard?.activeSeats ?? 0;
        const orgName = dashboard?.org?.company_name || '—';
        const orgId = formatOrgId(dashboard?.org?.id);
        const productCode = dashboard?.org?.product_code || 'prod_UExfDU5FMDJ9vK';

        return (
          <div className="overview-layout">
            <div className="org-panel-card overview-metrics-card">
              <h2 className="section-header overview-section-heading">Mentoring Control</h2>
              <div className="overview-metrics-stack">
                <p className="overview-metric-line">
                  <span className="overview-metric-label">Organisation:</span> {orgName}
                </p>
                <p className="overview-metric-line">
                  <span className="overview-metric-label">Org-ID:</span>{' '}
                  <span className="overview-id-box">{orgId}</span>
                </p>
                <p className="overview-metric-line">
                  <span className="overview-metric-label">Product:</span> Mentology Mentoring App
                </p>
                <p className="overview-metric-line">
                  <span className="overview-metric-label">Seats:</span> {seatLimit} ({activeSeats} used)
                </p>
                <p className="overview-metric-line">
                  <span className="overview-metric-label">Subscription Status:</span> Active
                </p>
              </div>
            </div>

            <div className="org-panel-card overview-profile-card">
              <form className="overview-profile-form" onSubmit={(e) => e.preventDefault()}>
                <div className="overview-form-row overview-form-row-2">
                  <label className="overview-field-label">
                    Contact Name
                    <input
                      className="input-field overview-input"
                      type="text"
                      value={profileForm.contactName}
                      onChange={(e) => updateProfile('contactName', e.target.value)}
                    />
                  </label>
                  <label className="overview-field-label">
                    Contact Title
                    <input
                      className="input-field overview-input"
                      type="text"
                      value={profileForm.contactTitle}
                      onChange={(e) => updateProfile('contactTitle', e.target.value)}
                    />
                  </label>
                </div>

                <p className="overview-section-label">Business Address</p>

                <label className="overview-field-label">
                  Street
                  <input
                    className="input-field overview-input"
                    type="text"
                    value={profileForm.street}
                    onChange={(e) => updateProfile('street', e.target.value)}
                  />
                </label>

                <div className="overview-form-row overview-form-row-3">
                  <label className="overview-field-label">
                    State
                    <input
                      className="input-field overview-input"
                      type="text"
                      value={profileForm.state}
                      onChange={(e) => updateProfile('state', e.target.value)}
                    />
                  </label>
                  <label className="overview-field-label">
                    Country
                    <input
                      className="input-field overview-input"
                      type="text"
                      value={profileForm.country}
                      onChange={(e) => updateProfile('country', e.target.value)}
                    />
                  </label>
                  <label className="overview-field-label">
                    Postcode
                    <input
                      className="input-field overview-input"
                      type="text"
                      value={profileForm.postcode}
                      onChange={(e) => updateProfile('postcode', e.target.value)}
                    />
                  </label>
                </div>

                <div className="overview-form-row overview-form-row-3">
                  <label className="overview-field-label">
                    Contact Phone
                    <input
                      className="input-field overview-input"
                      type="tel"
                      value={profileForm.contactPhone}
                      onChange={(e) => updateProfile('contactPhone', e.target.value)}
                    />
                  </label>
                  <label className="overview-field-label">
                    Contact Email
                    <input
                      className="input-field overview-input"
                      type="email"
                      value={profileForm.contactEmail}
                      onChange={(e) => updateProfile('contactEmail', e.target.value)}
                    />
                  </label>
                  <label className="overview-field-label">
                    Organisation Website
                    <input
                      className="input-field overview-input"
                      type="url"
                      value={profileForm.website}
                      onChange={(e) => updateProfile('website', e.target.value)}
                    />
                  </label>
                </div>

                <h3 className="section-header overview-ai-heading">AI Alignment</h3>
              </form>
            </div>
          </div>
        );
      }
      case 'Subscription': {
        const seatLimit = dashboard?.org?.seat_count ?? 10;
        return (
          <div className="org-panel-card subscription-governance-card">
            <h2 className="section-header subscription-governance-heading">Subscription Governance</h2>
            <table className="subscription-governance-table">
              <tbody>
                <tr>
                  <td className="subscription-col-action">Renew / Lock-In</td>
                  <td className="subscription-col-description">Convert Pilot to Full Subscription</td>
                  <td className="subscription-col-status">12 days remaining</td>
                  <td className="subscription-col-button">
                    <button type="button" className="subscription-action-btn subscription-action-btn-primary">
                      Renew
                    </button>
                  </td>
                </tr>
                <tr>
                  <td className="subscription-col-action">Upgrade Plan</td>
                  <td className="subscription-col-description">Increase seat limit</td>
                  <td className="subscription-col-status">Current: {seatLimit} seats</td>
                  <td className="subscription-col-button">
                    <button type="button" className="subscription-action-btn subscription-action-btn-primary">
                      Upgrade
                    </button>
                  </td>
                </tr>
                <tr>
                  <td className="subscription-col-action">Cancel</td>
                  <td className="subscription-col-description">Deactivate at end of 22-day window</td>
                  <td className="subscription-col-status">Active</td>
                  <td className="subscription-col-button">
                    <button type="button" className="subscription-action-btn subscription-action-btn-warning">
                      Cancel
                    </button>
                  </td>
                </tr>
                <tr>
                  <td className="subscription-col-action">Purge Workspace</td>
                  <td className="subscription-col-description">Delete all files</td>
                  <td className="subscription-col-status">Irreversible</td>
                  <td className="subscription-col-button">
                    <button type="button" className="subscription-action-btn subscription-action-btn-danger">
                      Delete All Files
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      }
      case 'Enrollment': {
        if (enrollmentSubview === 'archives') {
          const archivedMentors = dashboard?.archivedMentors ?? [];
          const archivedMentees = dashboard?.archivedMentees ?? [];

          return (
            <div className="org-panel-card archives-workspace admin-canvas-swap">
              <h2 className="section-header archives-heading">Historical Archives</h2>
              <p className="archives-subtext">Restore archived mentors and mentees to reactivate their live directory slots.</p>
              {mentorError && <p className="error-text archives-form-error">{mentorError}</p>}

              <div className="archives-dual-pane">
                <div className="archives-pane">
                  <h3 className="archives-pane-title">Archived Mentors Directory</h3>
                  <table className="admin-table archives-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Key</th>
                        <th>Email</th>
                        <th>Brief</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {archivedMentors.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="archives-empty-row">No archived mentors.</td>
                        </tr>
                      ) : (
                        archivedMentors.map((mentor) => (
                          <tr key={mentor.id}>
                            <td>{mentor.name}</td>
                            <td className="mentor-key-cell">{mentor.activation_key}</td>
                            <td>{mentor.email || '—'}</td>
                            <td className="mentor-brief-cell">{mentor.background_brief || '—'}</td>
                            <td>
                              <button
                                type="button"
                                className="mentor-restore-btn"
                                disabled={archiveActionLoading === `restore-mentor-${mentor.id}`}
                                onClick={() => handleRestoreMentor(mentor.id)}
                              >
                                {archiveActionLoading === `restore-mentor-${mentor.id}` ? 'Restoring...' : 'RESTORE'}
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="archives-pane">
                  <h3 className="archives-pane-title">Archived Mentees Directory</h3>
                  <table className="admin-table archives-table">
                    <thead>
                      <tr>
                        <th>Mentee</th>
                        <th>ELT Key</th>
                        <th>Mentor</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {archivedMentees.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="archives-empty-row">No archived mentees.</td>
                        </tr>
                      ) : (
                        archivedMentees.map((mentee) => (
                          <tr key={mentee.mentee_token}>
                            <td>{mentee.mentee_name || mentee.mentee_email || '—'}</td>
                            <td>{mentee.mentee_token}</td>
                            <td>{mentee.mentor_name || '—'}</td>
                            <td>{mentee.status}</td>
                            <td>
                              <button
                                type="button"
                                className="mentor-restore-btn"
                                disabled={archiveActionLoading === `restore-mentee-${mentee.mentee_token}`}
                                onClick={() => handleRestoreMentee(mentee.mentee_token)}
                              >
                                {archiveActionLoading === `restore-mentee-${mentee.mentee_token}` ? 'Restoring...' : 'RESTORE'}
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        }

        if (enrollmentSubview === 'mentor-list') {
          const mentors = dashboard?.mentors ?? [];
          const unassignedMentees = dashboard?.unassignedMentees ?? [];

          return (
            <div className="org-panel-card mentor-list-workspace admin-canvas-swap">
              <h2 className="section-header mentor-list-heading">Mentor Administration</h2>
              <p className="mentor-list-subtext">Create mentor activation keys and manage live mentor assignments.</p>

              <form onSubmit={createMentor} className="mentor-create-card">
                <p className="mentor-create-title">Add New Mentor</p>
                <div className="mentor-create-grid">
                  <label className="enrollment-field-label">
                    Name
                    <input
                      className="input-field enrollment-field-input"
                      type="text"
                      value={mentorForm.name}
                      onChange={(e) => updateMentorForm('name', e.target.value)}
                      required
                    />
                  </label>
                  <label className="enrollment-field-label">
                    Phone
                    <input
                      className="input-field enrollment-field-input"
                      type="tel"
                      value={mentorForm.phone}
                      onChange={(e) => updateMentorForm('phone', e.target.value)}
                    />
                  </label>
                  <label className="enrollment-field-label">
                    Email
                    <input
                      className="input-field enrollment-field-input"
                      type="email"
                      value={mentorForm.email}
                      onChange={(e) => updateMentorForm('email', e.target.value)}
                    />
                  </label>
                  <label className="enrollment-field-label mentor-brief-field">
                    Background Brief
                    <textarea
                      className="input-field enrollment-field-input enrollment-textarea"
                      value={mentorForm.backgroundBrief}
                      onChange={(e) => updateMentorForm('backgroundBrief', e.target.value)}
                      rows={2}
                    />
                  </label>
                </div>
                {mentorError && <p className="error-text mentor-form-error">{mentorError}</p>}
                <button type="submit" className="mentor-create-key-btn" disabled={mentorLoading}>
                  {mentorLoading ? 'Creating...' : 'CREATE MENTOR KEY'}
                </button>
              </form>

              <h3 className="mentor-directory-title">Active Mentor Directory</h3>
              <div className="mentor-directory-scroll">
                <table className="admin-table mentor-directory-table">
                  <thead>
                    <tr>
                      <th>Mentor Name</th>
                      <th>Generated Key</th>
                      <th>Status</th>
                      <th>Mentees</th>
                      <th>Assign New Mentee</th>
                      <th>Email</th>
                      <th>Archive</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mentors.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="mentor-directory-empty">No active mentors yet.</td>
                      </tr>
                    ) : (
                      mentors.map((mentor) => (
                        <tr key={mentor.id}>
                          <td>{mentor.name}</td>
                          <td>
                            <button
                              type="button"
                              className="mentor-key-copy-btn"
                              title="Copy activation key"
                              onClick={() => copyMentorKey(mentor.activation_key)}
                            >
                              {mentor.activation_key}
                              {copiedKey === mentor.activation_key ? ' ✓' : ''}
                            </button>
                          </td>
                          <td>
                            <span className={`mentor-status-badge mentor-status-${mentor.status.toLowerCase()}`}>
                              {mentor.status}
                            </span>
                          </td>
                          <td>{mentor.menteeCount ?? 0}</td>
                          <td>
                            <select
                              className="input-field mentor-assign-select"
                              defaultValue=""
                              onChange={(e) => {
                                const token = e.target.value;
                                if (token) {
                                  void handleAssignMentee(mentor.id, token);
                                  e.target.value = '';
                                }
                              }}
                            >
                              <option value="">Select unassigned mentee</option>
                              {unassignedMentees.map((mentee) => (
                                <option key={mentee.mentee_token} value={mentee.mentee_token}>
                                  {mentee.mentee_name || mentee.mentee_email || mentee.mentee_token}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            {mentor.email ? (
                              <a className="mentor-email-link" href={`mailto:${mentor.email}`}>
                                Email
                              </a>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="mentor-archive-btn"
                              disabled={archiveActionLoading === `mentor-${mentor.id}`}
                              onClick={() => handleArchiveMentor(mentor.id)}
                            >
                              {archiveActionLoading === `mentor-${mentor.id}` ? '...' : 'ARCHIVE'}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          );
        }

        const activeSeats = dashboard?.activeSeats ?? 0;
        const seatLimit = dashboard?.org?.seat_count ?? 10;
        const voiceEntry = resolveVoiceFromMatrix(enrollForm.languageTarget, enrollForm.aiMentorGender);
        const voiceProfile = voiceEntry ? formatVoiceProfileLabel(voiceEntry) : null;

        return (
          <div className="org-panel-card enrollment-workspace admin-canvas-swap">
            <div className="enrollment-card-header">
              <div className="enrollment-card-header-text">
                <h2 className="section-header enrollment-heading">Mentee Enrolment</h2>
                <p className="enrollment-subtext">Manage the individual Mentees.</p>
              </div>
              <button
                type="button"
                className="enrollment-crm-sim-btn"
                onClick={simulateCrmDataPush}
              >
                [ 🔌 Simulate CRM Data Push ]
              </button>
            </div>

            <form onSubmit={enrollMentee} className="enrollment-form-stack">
              <div className="enrollment-setup-grid">
                <div className="enrollment-setup-col enrollment-setup-col-left">
                  <label className="enrollment-field-label">
                    Mentee Name
                    <input
                      className="input-field enrollment-field-input"
                      type="text"
                      value={enrollForm.menteeName}
                      onChange={(e) => updateEnroll('menteeName', e.target.value)}
                      required
                    />
                  </label>
                  <label className="enrollment-field-label">
                    Business Name
                    <input
                      className="input-field enrollment-field-input"
                      type="text"
                      value={enrollForm.businessName}
                      onChange={(e) => updateEnroll('businessName', e.target.value)}
                    />
                  </label>
                  <label className="enrollment-field-label">
                    Address
                    <input
                      className="input-field enrollment-field-input"
                      type="text"
                      value={enrollForm.address}
                      onChange={(e) => updateEnroll('address', e.target.value)}
                    />
                  </label>
                  <label className="enrollment-field-label">
                    Contact Phone
                    <input
                      className="input-field enrollment-field-input"
                      type="tel"
                      value={enrollForm.contactPhone}
                      onChange={(e) => updateEnroll('contactPhone', e.target.value)}
                    />
                  </label>
                  <label className="enrollment-field-label">
                    Mentee Email
                    <input
                      className="input-field enrollment-field-input"
                      type="email"
                      value={enrollForm.menteeEmail}
                      onChange={(e) => updateEnroll('menteeEmail', e.target.value)}
                      required
                    />
                  </label>
                </div>

                <div className="enrollment-setup-col enrollment-setup-col-right">
                  <label className="enrollment-field-label">
                    Description of Business
                    <textarea
                      className="input-field enrollment-field-input enrollment-textarea"
                      value={enrollForm.businessDescription}
                      onChange={(e) => updateEnroll('businessDescription', e.target.value)}
                      rows={2}
                    />
                  </label>
                  <label className="enrollment-field-label">
                    Date Enrolled
                    <input
                      className="input-field enrollment-field-input"
                      type="date"
                      value={enrollForm.dateEnrolled}
                      onChange={(e) => updateEnroll('dateEnrolled', e.target.value)}
                    />
                  </label>
                  <label className="enrollment-field-label">
                    Language Target
                    <select
                      className="input-field enrollment-field-input enrollment-field-select"
                      value={enrollForm.languageTarget}
                      onChange={(e) => updateEnroll('languageTarget', e.target.value)}
                    >
                      <option value="">Select language</option>
                      {LANGUAGE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="enrollment-field-label">
                    AI-Mentor Gender
                    <select
                      className="input-field enrollment-field-input enrollment-field-select"
                      value={enrollForm.aiMentorGender}
                      onChange={(e) => updateEnroll('aiMentorGender', e.target.value)}
                    >
                      <option value="">Select gender</option>
                      {GENDER_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>

                  {voiceProfile && (
                    <div className="enrollment-voice-preview-bar">
                      <span className="enrollment-voice-profile-label">
                        Voice Profile: {voiceProfile}
                      </span>
                      <button
                        type="button"
                        className="enrollment-voice-preview-btn"
                        onClick={handleEnrollmentVoicePreview}
                      >
                        [ ▶ Preview Voice Sample ]
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="enrollment-section-break" aria-hidden="true" />

              <div className="enrollment-supervisor-section">
                <p className="enrollment-section-label">Assigned Human Supervisor</p>
                <div className="enrollment-supervisor-grid">
                  <label className="enrollment-field-label">
                    Assigned Mentor Name
                    <input
                      className="input-field enrollment-field-input"
                      type="text"
                      value={enrollForm.mentorName}
                      onChange={(e) => updateEnroll('mentorName', e.target.value)}
                    />
                  </label>
                  <label className="enrollment-field-label">
                    Mentor Contact Phone
                    <input
                      className="input-field enrollment-field-input"
                      type="tel"
                      value={enrollForm.mentorPhone}
                      onChange={(e) => updateEnroll('mentorPhone', e.target.value)}
                    />
                  </label>
                </div>
                <label className="enrollment-field-label enrollment-supervisor-email">
                  Mentor Email
                  <input
                    className="input-field enrollment-field-input"
                    type="email"
                    value={enrollForm.mentorEmail}
                    onChange={(e) => updateEnroll('mentorEmail', e.target.value)}
                  />
                </label>
                <p className="enrollment-supervisor-hint">
                  Optional — assign from Mentor List after enrolment, or enter supervisor details here.
                </p>
              </div>

              {enrollError && <p className="error-text enrollment-form-error">{enrollError}</p>}

              <div className="enrollment-action-row">
                <button type="submit" className="btn-primary enrollment-action-btn" disabled={loading}>
                  {loading ? 'Enrolling...' : 'Enroll Mentee'}
                </button>
                <button type="button" className="btn-primary enrollment-action-btn" onClick={resetEnrollForm}>
                  Reset Form
                </button>
              </div>
            </form>

            {newKey && (
              <div className="enrollment-key-display">
                <p className="org-profile-label">Active Mentee Key</p>
                <div className="key-display-box">{newKey}</div>
              </div>
            )}

            <table className="admin-table enrollment-mentee-table">
              <thead>
                <tr>
                  <th>Mentee</th>
                  <th>ELT Key</th>
                  <th>Assigned Mentor</th>
                  <th>Status</th>
                  <th>Account State</th>
                  <th>Archive</th>
                </tr>
              </thead>
              <tbody>
                {dashboard?.keys?.map((k) => (
                  <tr key={k.mentee_token}>
                    <td>{k.mentee_name || k.mentee_email || '—'}</td>
                    <td>{k.mentee_token}</td>
                    <td>{k.mentor_name || '—'}</td>
                    <td>{k.status}</td>
                    <td>{k.account_state}</td>
                    <td>
                      <button
                        type="button"
                        className="mentor-archive-btn"
                        disabled={archiveActionLoading === `archive-mentee-${k.mentee_token}`}
                        onClick={() => handleArchiveMentee(k.mentee_token)}
                      >
                        {archiveActionLoading === `archive-mentee-${k.mentee_token}` ? '...' : 'ARCHIVE'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p className="enrollment-seat-metric">
              {activeSeats} of {seatLimit} seats in use.
            </p>
          </div>
        );
      }
      case 'Policy Vault':
        return (
          <div className="org-panel-card policy-vault-workspace">
            <h2 className="section-header policy-vault-heading">Policy Vault</h2>
            <p className="policy-vault-intro">
              Manage and upload your organizational policies, procedures, and mandatory emergency protocols.
            </p>

            <div className="policy-vault-upload-stack">
              <div
                className={`policy-vault-upload-line ${vaultDragTarget === 'general' ? 'policy-vault-upload-active' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setVaultDragTarget('general'); }}
                onDragLeave={() => setVaultDragTarget(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  setVaultDragTarget(null);
                  addVaultFiles(e.dataTransfer.files, 'General');
                }}
              >
                <p className="policy-vault-upload-label">Upload Policies, Procedures & General Documents</p>
                <p className="policy-vault-upload-hint">Drop organisational policy and procedure files here.</p>
                <label className="btn-primary policy-vault-upload-btn">
                  Select General Documents
                  <input
                    type="file"
                    multiple
                    className="policy-vault-file-input"
                    onChange={(e) => addVaultFiles(e.target.files, 'General')}
                  />
                </label>
              </div>

              <div
                className={`policy-vault-upload-line policy-vault-upload-emergency ${vaultDragTarget === 'emergency' ? 'policy-vault-upload-active' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setVaultDragTarget('emergency'); }}
                onDragLeave={() => setVaultDragTarget(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  setVaultDragTarget(null);
                  addVaultFiles(e.dataTransfer.files, 'Emergency Protocol');
                }}
              >
                <p className="policy-vault-upload-label">Upload Mandatory Red Phone / Emergency Protocols Document</p>
                <p className="policy-vault-upload-hint">Dedicated line for the definitive crisis workflow file.</p>
                <label className="btn-primary policy-vault-upload-btn">
                  Select Emergency Protocol
                  <input
                    type="file"
                    className="policy-vault-file-input"
                    onChange={(e) => addVaultFiles(e.target.files, 'Emergency Protocol')}
                  />
                </label>
              </div>
            </div>

            <table className="admin-table policy-vault-file-table">
              <thead>
                <tr>
                  <th>File Name</th>
                  <th>Upload Timestamp</th>
                  <th>Category</th>
                </tr>
              </thead>
              <tbody>
                {vaultFiles.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="policy-vault-empty-row">
                      No vault files uploaded yet.
                    </td>
                  </tr>
                ) : (
                  vaultFiles.map((file) => (
                    <tr key={file.id}>
                      <td>{file.filename}</td>
                      <td>{file.uploadedAt}</td>
                      <td>
                        <span className={`policy-vault-category ${file.category === 'Emergency Protocol' ? 'policy-vault-category-emergency' : ''}`}>
                          {file.category}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        );
      case 'Guardian Feed':
        return (
          <div className="guardian-feed-workspace">
            <div
              className={`org-panel-card guardian-contacts-card ${!isEmergencyProtocolUploaded ? 'guardian-contacts-locked' : 'guardian-contacts-unlocked'}`}
              data-emergency-protocol-uploaded={isEmergencyProtocolUploaded}
            >
              <h3 className="guardian-card-title">Red Phone Protocol Contacts</h3>

              <div
                className={`guardian-status-banner ${isEmergencyProtocolUploaded ? 'guardian-status-unlocked' : 'guardian-status-locked'}`}
                role={isEmergencyProtocolUploaded ? 'status' : 'alert'}
              >
                {isEmergencyProtocolUploaded
                  ? 'Emergency Protocol active — Red Phone contact configuration unlocked.'
                  : 'Configuration Locked — Please upload your mandatory Red Phone / Emergency Protocols document in the Policy Vault to unlock emergency contacts.'}
              </div>

              <form className="guardian-contacts-form" onSubmit={updateRedPhoneContacts}>
                <fieldset className="guardian-contacts-fieldset" disabled={!isEmergencyProtocolUploaded}>
                  <label className="guardian-field-label">
                    Emergency Contact Name
                    <input
                      className="input-field guardian-field-input"
                      type="text"
                      value={redPhoneContacts.emergencyContactName}
                      onChange={(e) => updateRedPhoneContact('emergencyContactName', e.target.value)}
                      disabled={!isEmergencyProtocolUploaded}
                    />
                  </label>
                  <label className="guardian-field-label">
                    Emergency Lead Email
                    <input
                      className="input-field guardian-field-input"
                      type="email"
                      value={redPhoneContacts.emergencyLeadEmail}
                      onChange={(e) => updateRedPhoneContact('emergencyLeadEmail', e.target.value)}
                      disabled={!isEmergencyProtocolUploaded}
                    />
                  </label>
                  <label className="guardian-field-label">
                    Escalation Phone Number
                    <input
                      className="input-field guardian-field-input"
                      type="tel"
                      value={redPhoneContacts.escalationPhone}
                      onChange={(e) => updateRedPhoneContact('escalationPhone', e.target.value)}
                      disabled={!isEmergencyProtocolUploaded}
                    />
                  </label>

                  <div className="guardian-contacts-actions">
                    <button
                      type="submit"
                      className="btn-primary guardian-action-btn"
                      disabled={!isEmergencyProtocolUploaded}
                    >
                      Update Contacts
                    </button>
                    <button
                      type="button"
                      className="btn-primary guardian-action-btn"
                      disabled={!isEmergencyProtocolUploaded}
                      onClick={resetRedPhoneContacts}
                    >
                      Reset
                    </button>
                  </div>
                </fieldset>
              </form>
            </div>

            <div className="org-panel-card guardian-log-card">
              <h3 className="guardian-card-title">Guardian Feed Logs</h3>
              <table className="admin-table guardian-log-table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Mentee</th>
                    <th>ELT Key</th>
                    <th>Alert Type</th>
                    <th>Assigned Mentor</th>
                    <th>Mentor Notified</th>
                  </tr>
                </thead>
                <tbody>
                  {guardianFeed.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="guardian-log-empty">
                        No Red Phone alerts logged for enrolled Mentees.
                      </td>
                    </tr>
                  ) : (
                    guardianFeed.map((alert) => (
                      <tr key={alert.id} className={alert.mentor_notified ? 'guardian-row-alert' : ''}>
                        <td>{alert.created_at}</td>
                        <td>{alert.mentee_name || '—'}</td>
                        <td>{alert.mentee_token}</td>
                        <td>{alert.crisis_type}</td>
                        <td>{alert.mentor_name || alert.mentor_email || '—'}</td>
                        <td>{alert.mentor_notified ? `Yes (${alert.mentor_email})` : 'Pending'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'Support':
        return (
          <div className="org-panel-card support-workspace">
            <h2 className="section-header support-heading">Support</h2>
            <p className="support-intro">
              Lodge a support request with Mentology Operations. All tickets route to our help desk team.
            </p>
            {supportSuccess && (
              <div className="support-success-banner" role="status">
                {supportSuccess}
              </div>
            )}
            <form onSubmit={submitSupportTicket} className="support-form">
              <label className="support-field-label">
                Name
                <input
                  className="input-field support-field-input"
                  type="text"
                  placeholder="The individual lodging the complaint"
                  value={supportForm.name}
                  onChange={(e) => updateSupport('name', e.target.value)}
                  required
                />
              </label>
              <label className="support-field-label">
                Email
                <input
                  className="input-field support-field-input"
                  type="email"
                  value={supportForm.email}
                  onChange={(e) => updateSupport('email', e.target.value)}
                  required
                />
              </label>
              <label className="support-field-label">
                Contact Phone
                <input
                  className="input-field support-field-input"
                  type="tel"
                  value={supportForm.phone}
                  onChange={(e) => updateSupport('phone', e.target.value)}
                  required
                />
              </label>
              <label className="support-field-label">
                Problem Description / Issue Details
                <textarea
                  className="input-field support-field-input support-textarea"
                  value={supportForm.description}
                  onChange={(e) => updateSupport('description', e.target.value)}
                  required
                  rows={3}
                />
              </label>
              <div
                className={`support-dropzone ${dragActive ? 'support-dropzone-active' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragActive(false);
                  addSupportFiles(e.dataTransfer.files);
                }}
              >
                <p className="support-dropzone-hint">Drag and drop files here, or use the picker below.</p>
                <label className="btn-primary support-file-label">
                  Attach Screenshots or Log Files
                  <input
                    type="file"
                    multiple
                    className="support-file-input"
                    onChange={(e) => addSupportFiles(e.target.files)}
                  />
                </label>
                {supportFiles.length > 0 && (
                  <ul className="support-file-list">
                    {supportFiles.map((file) => (
                      <li key={`${file.name}-${file.size}`}>{file.name} ({Math.round(file.size / 1024)} KB)</li>
                    ))}
                  </ul>
                )}
              </div>
              {supportError && <p className="error-text support-error">{supportError}</p>}
              <div className="support-action-row">
                <button type="submit" className="btn-primary support-action-btn" disabled={supportLoading}>
                  {supportLoading ? 'Transmitting...' : 'Submit Support Ticket'}
                </button>
              </div>
            </form>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="admin-layout" data-screen-id={SCREENS.ELT06}>
      <aside className="admin-sidebar">
        <img
          src={encodeURI('/Assets/logo/Mentology Logo.png')}
          alt="Mentology"
          className="org-sidebar-logo brand-logo"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
        <p className="org-sidebar-heading">Mentoring Admin Control</p>
        {SIDEBAR_SECTIONS.map((section) => (
          <div key={section} className="admin-nav-group">
            <button
              type="button"
              className={`admin-nav-item ${activeSection === section && (section !== 'Enrollment' || enrollmentSubview === 'default') ? 'active' : ''}`}
              onClick={() => {
                setActiveSection(section);
                if (section === 'Enrollment') setEnrollmentSubview('default');
              }}
            >
              {section}
            </button>
            {section === 'Enrollment' && ENROLLMENT_SUB_NAV.map((subItem) => (
              <button
                key={subItem}
                type="button"
                className={`admin-nav-item admin-nav-subitem ${
                  activeSection === 'Enrollment' && enrollmentSubview === (subItem === 'Mentor List' ? 'mentor-list' : 'archives')
                    ? 'active'
                    : ''
                }`}
                onClick={() => {
                  setActiveSection('Enrollment');
                  setEnrollmentSubview(subItem === 'Mentor List' ? 'mentor-list' : 'archives');
                  setMentorError('');
                }}
              >
                {subItem}
              </button>
            ))}
          </div>
        ))}
        <button type="button" className="admin-nav-item logout" onClick={() => navigate('/')}>Logout</button>
      </aside>
      <main className="admin-panel">
        {clientKey && (
          <div className="org-client-key-banner">
            <span className="org-client-key-label">Active Client Key:</span>
            <span className="org-client-key-value">{clientKey}</span>
          </div>
        )}
        {!dashboard ? (
          <p className="scannable-bullet-text">Loading organisation console...</p>
        ) : (
          renderPanel()
        )}
      </main>
    </div>
  );
}
